import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

@Injectable({
    providedIn: 'root',
})
export class ExportService {
    /**
     * Exports an array of data to a CSV file.
     *
     * @param data - The array of objects to export.
     * @param fileName - The desired name of the exported file (without extension).
     * @param columns - Optional array of column definitions to map fields to headers.
     */
    public exportToCsv(
        data: any[],
        fileName: string = 'export',
        columns?: { field: string; header: string }[],
    ): void {
        if (!data || !data.length) {
            console.warn('No data to export');
            return;
        }

        const headers = columns ? columns.map((col) => col.header) : Object.keys(data[0]);
        const fields = columns ? columns.map((col) => col.field) : Object.keys(data[0]);

        const csvContent = [
            headers.join(','), // Header row
            ...data.map((row) =>
                fields
                    .map((field) => {
                        let cellValue = row[field];

                        // Handle null/undefined
                        if (cellValue === null || cellValue === undefined) {
                            cellValue = '';
                        }

                        // Handle arrays (e.g., tags)
                        if (Array.isArray(cellValue)) {
                            cellValue = cellValue.join('; ');
                        }

                        // Handle objects or nested data if necessary (simple string conversion)
                        if (typeof cellValue === 'object' && cellValue !== null) {
                            cellValue = JSON.stringify(cellValue);
                        }

                        // Neutralize spreadsheet formula injection before escaping (NDA-4.3)
                        const safeValue = this.sanitizeForFormulaInjection(String(cellValue));

                        // Escape quotes and wrap in quotes if contains commas or newlines
                        const cellString = safeValue.replace(/"/g, '""');
                        return /[,#\n"]/.test(cellString) ? `"${cellString}"` : cellString;
                    })
                    .join(','),
            ),
        ].join('\r\n');

        this.downloadFile(csvContent, `${fileName}.csv`, 'text/csv;charset=utf-8;');
    }

    /**
     * Exports an array of data to an Excel (.xlsx) file.
     *
     * @param data - The array of objects to export.
     * @param fileName - The desired name of the exported file (without extension).
     * @param columns - Optional array of column definitions to map fields to headers.
     */
    public exportToExcel(
        data: any[],
        fileName: string = 'export',
        columns?: { field: string; header: string }[],
    ): void {
        if (!data || !data.length) {
            console.warn('No data to export');
            return;
        }

        const fields = columns ? columns.map((col) => col.field) : Object.keys(data[0]);
        const headers = columns ? columns.map((col) => col.header) : Object.keys(data[0]);

        // Map data to match headers
        const mappedData = data.map((row) => {
            const mappedRow: any = {};
            fields.forEach((field, index) => {
                const header = headers[index];
                let cellValue = row[field];

                // Handle arrays (e.g., tags)
                if (Array.isArray(cellValue)) {
                    cellValue = cellValue.join('; ');
                }

                // Neutralize spreadsheet formula injection on string cells (NDA-4.3)
                if (typeof cellValue === 'string') {
                    cellValue = this.sanitizeForFormulaInjection(cellValue);
                }

                mappedRow[header] = cellValue;
            });
            return mappedRow;
        });

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(mappedData);
        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

        // Export file
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    }

    /**
     * Neutralizes spreadsheet formula injection (CSV/XLSX) by prefixing a single
     * quote to any value whose first character can trigger formula evaluation in
     * Excel / Google Sheets / LibreOffice.
     *
     * @remarks
     * Security (NDA-4.3): cells beginning with `=`, `+`, `-`, `@`, tab (`\t`), or
     * carriage return (`\r`) are treated as formulas by spreadsheet apps. Leading
     * whitespace is trimmed before checking to prevent bypass attacks. Prefixing
     * a `'` forces the value to be read as literal text. Applied to both the CSV
     * and XLSX export paths.
     *
     * Note: Pipe (`|`) is used within DDE payloads but requires a formula prefix
     * (`=`, `+`, `-`, `@`) to trigger evaluation, so it is already covered.
     * Newline (`\n`) embedded mid-value is handled by proper CSV quoting (line 55),
     * not prefix checking.
     *
     * @param value - The raw cell value as a string.
     * @returns The value, prefixed with `'` when it starts with a formula trigger.
     */
    private sanitizeForFormulaInjection(value: string): string {
        const trimmed = value.trimStart();
        return /^[=+\-@\t\r]/.test(trimmed) ? `'${value}` : value;
    }

    /**
     * Internal helper to trigger the file download in the browser.
     */
    private downloadFile(content: string, fileName: string, contentType: string): void {
        const blob = new Blob([content], { type: contentType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}
